<template>
  <div>
    <ClinicPageHeader
      title="Financeiro da Clínica"
      subtitle="Operação financeira — faturamento, recebimentos e repasses aos profissionais de saúde"
    >
      <template #actions>
        <select
          v-model="month"
          class="select"
          data-test="month"
          style="width:180px;height:40px"
          @change="onMonthChange"
        >
          <option v-for="m in months" :key="m.value" :value="m.value">
            {{ m.label }}
          </option>
        </select>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <div class="tabs" style="margin-bottom:var(--space-6)">
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceDashboard', query: monthQuery }"
        >
          Visão geral
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceReceivables', query: monthQuery }"
        >
          Recebimentos
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceTransfers', query: monthQuery }"
        >
          Repasses
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceStatement', query: monthQuery }"
        >
          Extrato
        </router-link>
      </div>

      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar os recebimentos"
        message="Ocorreu um erro ao buscar os recebimentos da clínica. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="fetch">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <!-- 403: sem acesso ao Financeiro nesta clínica (US-C11) -->
      <ClinicStateBox
        v-else-if="state === 'forbidden'"
        state="empty"
        title="Acesso restrito"
        message="Você não tem acesso ao Financeiro desta clínica. Fale com o administrador."
      />

      <template v-else-if="state === 'loading'">
        <div class="metrics3" style="margin-bottom:var(--space-5)">
          <div v-for="i in 3" :key="i" class="metric">
            <div class="skel-line" style="width:70%;margin-bottom:8px" />
            <div class="skel-line" style="width:45%;height:24px" />
          </div>
        </div>
        <div class="card">
          <div class="skel-line" style="width:100%;height:200px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <div class="metrics3" style="margin-bottom:var(--space-5)">
          <div class="metric">
            <span class="metric-label">Recebido no mês</span>
            <span class="metric-value sm money--pos">
              {{ kpiMoney(receivedCents) }}
            </span>
            <span class="metric-foot">{{ receivedCount }} consultas pagas</span>
          </div>
          <div class="metric">
            <span class="metric-label">A receber</span>
            <span class="metric-value sm">{{ kpiMoney(pendingCents) }}</span>
            <span class="metric-foot">
              {{ pendingCount }} consultas pendentes
            </span>
          </div>
          <div class="metric">
            <span class="metric-label">Inadimplência</span>
            <span class="metric-value sm">{{ delinquencyPct }}</span>
            <span class="metric-foot">consultas em aberto</span>
          </div>
        </div>

        <div class="dtoolbar">
          <div class="dsearch">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M21 21l-4-4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <input
              :value="filters.q"
              class="input"
              placeholder="Buscar por paciente…"
              @input="onFilter('q', $event.target.value)"
            />
          </div>
          <select
            :value="filters.doctorId"
            class="select"
            @change="onFilter('doctorId', $event.target.value)"
          >
            <option value="">Todos os profissionais de saúde</option>
            <option v-for="d in doctorOptions" :key="d.id" :value="d.id">
              {{ d.name }}
            </option>
          </select>
          <select
            :value="filters.method"
            class="select"
            @change="onFilter('method', $event.target.value)"
          >
            <option value="">Todas as formas</option>
            <option v-for="f in methodOptions" :key="f" :value="f">
              {{ f }}
            </option>
          </select>
          <select
            :value="filters.status"
            class="select"
            @change="onFilter('status', $event.target.value)"
          >
            <option value="">Todos os status</option>
            <option v-for="s in statusOptions" :key="s.value" :value="s.value">
              {{ s.label }}
            </option>
          </select>
        </div>

        <div class="card card--flush">
          <table v-if="rows.length" class="table">
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Profissional de saúde</th>
                <th>Data</th>
                <th>Forma</th>
                <th>Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in rows" :key="r.id">
                <td>
                  <div class="row" style="gap:var(--space-3)">
                    <div class="avatar avatar--sm" :class="r.av">
                      {{ r.initials }}
                    </div>
                    <span class="cell-strong">{{ r.patientName }}</span>
                  </div>
                </td>
                <td>{{ r.doctorName }}</td>
                <td class="num">{{ r.dateBR }}</td>
                <td>{{ r.method }}</td>
                <td class="num money">{{ formatMoney(r.grossCents) }}</td>
                <td>
                  <span class="badge" :class="`badge--${r.statusVariant}`">
                    {{ r.statusLabel }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <ClinicStateBox
            v-else
            state="empty"
            title="Nenhum recebimento encontrado"
            message="Ajuste os filtros para ver os recebimentos da clínica."
          />

          <div
            v-if="rows.length"
            style="padding:var(--space-4) var(--space-5);border-top:1px solid var(--color-gray-100);display:flex;align-items:center;justify-content:space-between"
          >
            <span
              style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
            >
              {{ rows.length }} recebimentos no mês
            </span>
            <div class="row" style="gap:var(--space-2)">
              <button class="btn btn--ghost btn--sm">← Anterior</button>
              <button class="btn btn--outline btn--sm">Próxima →</button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import clinicaListQuery from "@/mixins/clinicaListQuery";
import { ClinicFinance } from "@/services/clinica";
import { formatMoney } from "@/data/clinica";
import clinicFinanceMonth from "@/mixins/clinicFinanceMonth";

// Recebimento: status do backend → rótulo + variante do badge.
const STATUS = {
  recebido: { label: "Recebido", variant: "success" },
  pendente: { label: "Pendente", variant: "warning" },
  estornado: { label: "Estornado", variant: "danger" }
};

export default {
  name: "ClinicFinanceReceivables",
  components: { ClinicPageHeader, ClinicStateBox },
  mixins: [clinicaListQuery, clinicFinanceMonth],
  data() {
    return {
      // loading → ready/error. Preview de erro via ?state=error.
      state: "loading",
      // Filtros refletidos na URL (q, doctorId, method, status).
      filters: { q: "", doctorId: "", method: "", status: "" },
      rows: [],
      // Snapshot completo (sem filtro) p/ KPIs do mês + opções dos selects.
      allRows: [],
      metaLoaded: false,
      statusOptions: [
        { value: "recebido", label: "Recebido" },
        { value: "pendente", label: "Pendente" },
        { value: "estornado", label: "Estornado" }
      ]
    };
  },
  created() {
    this.filters = this.clinicaReadQuery({
      q: "",
      doctorId: "",
      method: "",
      status: ""
    });
    this.fetch();
  },
  methods: {
    formatMoney,
    kpiMoney(cents) {
      return ((cents || 0) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    // Os KPIs do topo vêm do mês inteiro: troca o mês, recarrega o snapshot.
    onMonthChanged() {
      this.metaLoaded = false;
      this.fetch();
    },
    async fetch() {
      this.state = "loading";
      try {
        const fail = this.$route.query.state === "error";
        const { data } = await ClinicFinance.receivables({
          month: this.month,
          q: this.filters.q,
          doctorId: this.filters.doctorId,
          method: this.filters.method,
          status: this.filters.status,
          fail
        });
        this.rows = data.map(r => this.enrich(r));
        if (!this.metaLoaded) {
          const all = await ClinicFinance.receivables({ month: this.month });
          this.allRows = all.data;
          this.metaLoaded = true;
        }
        this.state = "ready";
      } catch (e) {
        // 403 = RBAC (papel Atendimento) — não é falha de carregamento
        this.state =
          e && e.response && e.response.status === 403 ? "forbidden" : "error";
      }
    },
    onFilter(key, value) {
      this.filters = { ...this.filters, [key]: value };
      this.clinicaSyncQuery({ [key]: value });
    },
    enrich(r) {
      const s = STATUS[r.status] || { label: r.status, variant: "neutral" };
      return {
        ...r,
        doctorName: r.doctorName || "—",
        initials: r.initials || this.initialsOf(r.patientName),
        av: r.av || "av-slate",
        dateBR: this.brDate(r.date),
        statusLabel: s.label,
        statusVariant: s.variant
      };
    },
    initialsOf(name) {
      return (name || "")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0])
        .join("")
        .toUpperCase();
    },
    brDate(iso) {
      const [y, m, d] = String(iso).split("-");
      return `${d}/${m}/${y}`;
    }
  },
  computed: {
    receivedCents() {
      return this.allRows
        .filter(r => r.status === "recebido")
        .reduce((sum, r) => sum + r.grossCents, 0);
    },
    receivedCount() {
      return this.allRows.filter(r => r.status === "recebido").length;
    },
    pendingCents() {
      return this.allRows
        .filter(r => r.status === "pendente")
        .reduce((sum, r) => sum + r.grossCents, 0);
    },
    pendingCount() {
      return this.allRows.filter(r => r.status === "pendente").length;
    },
    delinquencyPct() {
      const total = this.allRows.length;
      if (!total) return "0%";
      const open = this.allRows.filter(
        r => r.status === "pendente" || r.status === "estornado"
      ).length;
      return `${((open / total) * 100).toLocaleString("pt-BR", {
        maximumFractionDigits: 1
      })}%`;
    },
    doctorOptions() {
      const seen = {};
      this.allRows.forEach(r => {
        if (r.doctorId) seen[r.doctorId] = r.doctorName;
      });
      return Object.keys(seen).map(id => ({ id, name: seen[id] }));
    },
    methodOptions() {
      return [...new Set(this.allRows.map(r => r.method))];
    }
  },
  watch: {
    "$route.query"() {
      this.filters = this.clinicaReadQuery({
        q: "",
        doctorId: "",
        method: "",
        status: ""
      });
      this.fetch();
    }
  }
};
</script>
